import { Link, useLocation } from "react-router-dom"
import {Calculator, Menu, X, ChevronDown} from 'lucide-react'
import { useState, useRef, useEffect } from "react"

interface LayoutProps {
  children: React.ReactNode
}

const assetDropdown = [
  { name: "Computer & Hardware", href: "/calculator/computer-hardware" },
  { name: "Furniture & Appliances", href: "/calculator/furniture-appliances" },
  { name: "Heavy Equipment", href: "/calculator/heavy-equipment" },
  { name: "Business Vehicles", href: "/calculator/business-vehicles" },
]

function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [assetMenuOpen, setAssetMenuOpen] = useState(false)
  const location = useLocation()
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setAssetMenuOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  const isActive = (href: string) => location.pathname === href
  const isCalcActive = location.pathname.startsWith("/calculator") || location.pathname === "/calculators"

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <Calculator className="w-8 h-8 text-blue-600" />
              <span className="text-xl font-bold text-gray-900">Depreciation Hub</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/"
                className={`text-sm font-medium transition-colors ${isActive("/") ? "text-blue-600" : "text-gray-600 hover:text-blue-600"}`}>
                Home
              </Link>

              {/* Calculators Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setAssetMenuOpen(!assetMenuOpen)}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors ${isCalcActive ? "text-blue-600" : "text-gray-600 hover:text-blue-600"}`}
                >
                  Calculators
                  <ChevronDown className={`w-4 h-4 transition-transform ${assetMenuOpen ? "rotate-180" : ""}`} />
                </button>

                {assetMenuOpen ? (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-gray-200 shadow-lg py-2 z-50">
                    <Link to="/calculators" onClick={() => setAssetMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 font-medium border-b border-gray-100 mb-1">
                      All Calculators
                    </Link>
                    <div className="px-4 py-1">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Asset Specific Tools</p>
                    </div>
                    {assetDropdown.map((item) => (
                      <Link key={item.href} to={item.href} onClick={() => setAssetMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                        {item.name}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>

              <Link to="/glossary"
                className={`text-sm font-medium transition-colors ${isActive("/glossary") ? "text-blue-600" : "text-gray-600 hover:text-blue-600"}`}>
                Glossary
              </Link>
              <Link to="/about"
                className={`text-sm font-medium transition-colors ${isActive("/about") ? "text-blue-600" : "text-gray-600 hover:text-blue-600"}`}>
                About
              </Link>
              <Link to="/calculators"
                className="ml-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                Get Started
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-gray-600" /> : <Menu className="w-6 h-6 text-gray-600" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen ? (
            <div className="md:hidden py-4 border-t border-gray-200 space-y-1">
              {[
                { name: "Home", href: "/" },
                { name: "All Calculators", href: "/calculators" },
                { name: "Straight Line", href: "/calculator/straight-line" },
                { name: "Double Declining Balance", href: "/calculator/double-declining" },
                { name: "MACRS", href: "/calculator/macrs" },
                { name: "Rental Property", href: "/calculator/rental-property" },
                { name: "Computer & Hardware", href: "/calculator/computer-hardware" },
                { name: "Furniture & Appliances", href: "/calculator/furniture-appliances" },
                { name: "Heavy Equipment", href: "/calculator/heavy-equipment" },
                { name: "Business Vehicles", href: "/calculator/business-vehicles" },
                { name: "Glossary", href: "/glossary" },
                { name: "About", href: "/about" },
              ].map((item) => (
                <Link key={item.href} to={item.href} onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive(item.href) ? "text-blue-600 bg-blue-50" : "text-gray-600 hover:bg-gray-100"
                  }`}>
                  {item.name}
                </Link>
              ))}
            </div>
          ) : null}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="w-6 h-6 text-blue-600" />
                <span className="font-bold text-gray-900">Depreciation Hub</span>
              </div>
              <p className="text-sm text-gray-600">
                Professional-grade depreciation calculators to help you maximize tax
                deductions, simplify IRS compliance, and master accounting fundamentals.
              </p>
            </div>

            {/* Core Calculators */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Core Methods
              </h3>
              <ul className="space-y-2">
                {[
                  ["Straight Line", "/calculator/straight-line"],
                  ["Double Declining Balance", "/calculator/double-declining"],
                  ["MACRS Depreciation", "/calculator/macrs"],
                  ["Rental Property", "/calculator/rental-property"],
                ].map(([name, href]) => (
                  <li key={href}>
                    <Link to={href} className="text-sm text-gray-600 hover:text-blue-600 transition-colors">{name}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Asset Specific */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Asset Specific Tools
              </h3>
              <ul className="space-y-2">
                {[
                  ["Computer & Hardware", "/calculator/computer-hardware"],
                  ["Furniture & Appliances", "/calculator/furniture-appliances"],
                  ["Heavy Equipment", "/calculator/heavy-equipment"],
                  ["Business Vehicles", "/calculator/business-vehicles"],
                ].map(([name, href]) => (
                  <li key={href}>
                    <Link to={href} className="text-sm text-gray-600 hover:text-blue-600 transition-colors">{name}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Resources
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/glossary" className="text-sm text-gray-600 hover:text-blue-600 transition-colors">
                    Depreciation Glossary
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-sm text-gray-600 hover:text-blue-600 transition-colors">
                    About Us
                  </Link>
                </li>
                <li className="text-sm text-gray-600">Depreciation Tax Shield</li>
                <li className="text-sm text-gray-600">IRS Form 4562</li>
                <li className="text-sm text-gray-600">Section 179 Deduction</li>
                <li className="text-sm text-gray-600">MACRS Tables</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              © {new Date().getFullYear()} Depreciation Hub · Free professional depreciation calculators for
              tax, accounting, and IRS compliance · Not tax advice.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Layout
